import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { TEMA } from '../theme';
import {
  Prissättning, PrisInköp, PrisRad, PrisProducent, Prisutkast, Tidigare, Kalkyl as KalkylTyp, VINTYPNAMN, Vintyp,
  hämtaPrissättning, exporteraPrishistorik, sparaInställningar, ändraRad, skapaRad, taBortRad, skapaPrisutkast, ändraPrisutkast, taBortPrisutkast, låsPriser, låsUppPriser, slåIhop,
  kr, valutabelopp, datumText, felText,
} from '../api/inkop';
import { useAuth } from '../context/AuthContext';
import { useDemo } from '../demo';
import { RadFormulär, RadUtkast, radTillUtkast, utkastTillData } from './InkopDetalj';
import { sida, rubrik, underrubrik, kort, fält, knapp, knappSpök, länkknapp, etikett, tabell, th, td, tal, märke } from '../components/inkopStil';

// Prissättningen (beslut 27 okt 2026) ersätter Excel-prismodellen. Varje
// producent har ett kalkylark: ett vin per rad, och varje kostnad går att ändra
// direkt i cellen, som i Excel. Ett tomt fält visar inköpets eller standardens
// värde i grått; skriver man något gäller det för just det vinet.
//
// Sidan har två flikar (beslut 29 okt 2026): Viner att prissätta, där arket
// är ett sätt att kalibrera listpriset innan det läggs upp i Fortnox, och
// Historik med alla låsta prissättningar och tidigare inköp, nyast först, som
// går att exportera till Excel. Vin med låst pris som inte finns i Fortnox än
// är märkta i historiken; där hittar man vad som ska läggas upp.

const RÖD = '#D9695F';
const pct = (v: number | null | undefined) => (v == null ? '–' : `${v.toLocaleString('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);
const kronor = (v: number | null | undefined, dec = 2) =>
  v == null ? '–' : v.toLocaleString('sv-SE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const tal2 = (v: number | null | undefined) => (v == null ? '' : String(Math.round(v * 100) / 100).replace('.', ','));
const tolka = (t: string) => {
  const n = Number(t.replace(/\s/g, '').replace(',', '.'));
  return t.trim() === '' ? null : Number.isFinite(n) && n >= 0 ? n : NaN;
};
const grå: React.CSSProperties = { fontSize: '11.5px', color: TEMA.inkFaint };
const cell: React.CSSProperties = { ...td, ...tal, padding: '5px 4px', verticalAlign: 'middle', fontSize: '12.5px' };
const rubrikcell: React.CSSProperties = { ...th, ...tal, padding: '6px 4px', fontSize: '11px', whiteSpace: 'normal', verticalAlign: 'bottom', lineHeight: 1.25 };

// En cell att skriva i. Tomt fält visar standardvärdet i grått. onÄndra anropas
// för varje tecken, så att arket räknar om direkt; onSpara när man lämnar
// fältet eller trycker Enter. Esc ångrar.
// Med standard markeras ett eget värde med guldkant, så att det syns vad som avviker.
function Cell({ värde: start, standard, etikett, onSpara, onÄndra, bredd = 54, låst = false }: {
  värde: number | null | undefined; standard?: string; etikett: string; onSpara: (v: number | null) => void | Promise<unknown>;
  onÄndra?: (v: number | null | undefined) => void; bredd?: number; låst?: boolean;
}) {
  const [värde, setVärde] = useState(start != null ? tal2(start) : '');
  useEffect(() => { setVärde(start != null ? tal2(start) : ''); }, [start]);
  if (låst) {
    // Låsta priser visas som text; lås upp beställningen för att ändra.
    return <span title={etikett} style={{ display: 'inline-block', minWidth: `${bredd}px`, padding: '3px 5px', color: start != null ? TEMA.ink : TEMA.inkFaint }}>
      {start != null ? tal2(start) : standard || ''}
    </span>;
  }
  const klar = async () => {
    const v = tolka(värde);
    if (Number.isNaN(v)) { setVärde(start != null ? tal2(start) : ''); onÄndra?.(undefined); return; }
    if (v !== (start ?? null)) await onSpara(v);
    onÄndra?.(undefined);
  };
  return (
    <input
      className="vs-cell"
      style={{ ...fält, width: `${bredd}px`, padding: '3px 5px', fontSize: '12.5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
        background: TEMA.bg, borderColor: start != null && standard !== undefined ? TEMA.accent : '#3A3B3F' }}
      inputMode="decimal" aria-label={etikett} title={etikett} value={värde} placeholder={standard}
      onClick={e => e.stopPropagation()}
      onFocus={e => e.target.select()}
      onChange={e => { setVärde(e.target.value); const v = tolka(e.target.value); onÄndra?.(Number.isNaN(v) ? undefined : v); }}
      onBlur={klar}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') { setVärde(start != null ? tal2(start) : ''); onÄndra?.(undefined); (e.target as HTMLInputElement).blur(); }
      }}
    />
  );
}

// Kalkylen räknad om i webbläsaren med det man håller på att skriva, så att
// inpris, förslag och marginal ändras direkt. Servern räknar samma sak när det sparas.
type Lokalt = Partial<Record<'pris_valuta' | 'frakt_per_flaska' | 'agent_procent' | 'ls' | 'frakt_till_kund' | 'ovrigt' | 'punktskatt' | 'provpris', number | null>>;
const FÖRSLAG = 29.5;
function räknaOm(k: KalkylTyp, pris: number | null | undefined, l: Lokalt, förslagMarginal = FÖRSLAG) {
  const v = (x: number | null | undefined, standard: number) => (x === undefined || x === null ? standard : x);
  const pris_kr = l.pris_valuta !== undefined ? (l.pris_valuta ?? pris ?? 0) * k.kurs : k.pris_kr;
  const andel = l.agent_procent !== undefined ? v(l.agent_procent, k.agent_andel * 100) / 100 : k.agent_andel;
  const frakt = v(l.frakt_per_flaska, k.frakt_till_lager);
  const inpris = pris_kr + frakt + pris_kr * andel + v(l.ls, k.ls) + v(l.ovrigt, k.ovrigt) + v(l.punktskatt, k.punktskatt) + v(l.frakt_till_kund, k.frakt_till_kund);
  return { inpris, förslag: inpris > 0 ? Math.ceil(Math.round(inpris / (1 - förslagMarginal / 100) * 1e6) / 1e6) : null };
}
const marginal = (listpris: number | null | undefined, inpris: number) => (listpris ? ((listpris - inpris) / listpris) * 100 : null);

// "Förra gången: 2024, 5 nov 2025 · 7,40 € · 179 kr i Fortnox · 31,4 %". Fortnox-priset är det som gällde.
function TidigareRad({ t, förslagGräns }: { t?: Tidigare[]; förslagGräns: number }) {
  if (!t || !t.length) return null;
  const x = t[0];
  const lp = x.listpris_fortnox ?? x.last_listpris ?? null;
  return (
    <div style={{ ...grå, marginTop: '2px' }}>
      Förra gången ({[x.argang, datumText(x.datum)].filter(Boolean).join(', ')}): {valutabelopp(x.pris_valuta ?? null, x.valuta)}
      {lp != null && <> · {kronor(lp, 0)} kr · <span style={{ color: x.marginal_pct != null && x.marginal_pct < förslagGräns ? RÖD : TEMA.inkSoft }}>{pct(x.marginal_pct)}</span></>}
    </div>
  );
}

// Uppställningen steg för steg, när man klickar på ett vin.
function Uppställning({ k, pris, valuta }: { k: KalkylTyp; pris?: number | null; valuta: string }) {
  const rader: [string, string][] = [
    [`Pris ${valutabelopp(pris ?? null, valuta)} × kurs ${tal2(k.kurs)}`, kr(k.pris_kr, 2)],
    ['+ Frakt till lager', kr(k.frakt_till_lager, 2)], ['+ Agent', kr(k.agent, 2)], ['+ Övrigt', kr(k.ovrigt, 2)],
    ['= Bokföringens inpris', kr(k.inpris_bokforing, 2)],
    ['+ LS', kr(k.ls, 2)], ['+ Punktskatt', kr(k.punktskatt, 2)], ['+ Frakt till kund', kr(k.frakt_till_kund, 2)],
    ['= Totalt inpris', kr(k.inpris_totalt, 2)],
  ];
  return (
    <div style={{ maxWidth: '360px', fontSize: '12px' }}>
      {rader.map(([a, b]) => (
        <div key={a} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', color: a.startsWith('=') ? TEMA.ink : TEMA.inkSoft }}>
          <span>{a}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{b}</span>
        </div>
      ))}
    </div>
  );
}

// Namn, typ, årgång, volym och alkohol för ett vin att räkna på.
type UtkastFält = { vin: string; typ: string; argang: string; volym_cl: string; alkoholhalt: string };
const tillFält = (u: Prisutkast): UtkastFält => ({
  vin: u.vin, typ: u.typ || '', argang: u.argang || '', volym_cl: String(u.volym_cl ?? 75), alkoholhalt: u.alkoholhalt != null ? String(u.alkoholhalt) : '',
});
function UtkastFormulär({ f, setF, onSpara, onAvbryt }: { f: UtkastFält; setF: (f: UtkastFält) => void; onSpara: () => void; onAvbryt: () => void }) {
  return (
    <form onSubmit={e => { e.preventDefault(); onSpara(); }} onClick={e => e.stopPropagation()}
      style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'end', padding: '4px 0 8px' }}>
      <div style={{ flex: '2 1 220px' }}><label style={etikett}>Vin</label>
        <input style={fält} value={f.vin} required onChange={e => setF({ ...f, vin: e.target.value })} /></div>
      <div style={{ width: '120px' }}><label style={etikett}>Typ</label>
        <select style={fält} value={f.typ} onChange={e => setF({ ...f, typ: e.target.value })}>
          <option value="">–</option>
          {Object.entries(VINTYPNAMN).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select></div>
      <div style={{ width: '80px' }}><label style={etikett}>Årgång</label>
        <input style={fält} value={f.argang} onChange={e => setF({ ...f, argang: e.target.value })} /></div>
      <div style={{ width: '70px' }}><label style={etikett}>Volym cl</label>
        <input style={fält} value={f.volym_cl} onChange={e => setF({ ...f, volym_cl: e.target.value })} /></div>
      <div style={{ width: '70px' }}><label style={etikett}>Alkohol %</label>
        <input style={fält} value={f.alkoholhalt} onChange={e => setF({ ...f, alkoholhalt: e.target.value })} /></div>
      <button type="submit" style={knapp}>Spara</button>
      <button type="button" style={knappSpök} onClick={onAvbryt}>Avbryt</button>
    </form>
  );
}

type Kör = (f: () => Promise<unknown>, fel: string) => Promise<boolean>;
// En rad i arket: ett vin i det pågående inköpet, eller ett vin att räkna på.
type Arkrad =
  | { typ: 'inkop'; id: string; r: PrisRad; i: PrisInköp }
  | { typ: 'utkast'; id: string; u: Prisutkast };

// Ladda ner prishistoriken som en Excel-fil, för en producent eller alla.
async function laddaNerHistorik(producentId?: string, namn?: string) {
  const svar = await exporteraPrishistorik(producentId);
  const url = URL.createObjectURL(svar.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Prishistorik${namn ? ` ${namn}` : ''} ${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const avsnitt: React.CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink, fontWeight: 400, margin: 0 };

function Kalkylark({ p, förslagGräns, kör }: { p: PrisProducent; förslagGräns: number; kör: Kör }) {
  const [öppen, setÖppen] = useState<string | null>(null);
  const [ändrarRad, setÄndrarRad] = useState<{ id: string; u: RadUtkast } | null>(null);
  const [ändrarUtkast, setÄndrarUtkast] = useState<{ id: string; f: UtkastFält } | null>(null);
  const [nytt, setNytt] = useState({ vin: '', pris: '', iInköpet: true });
  const [läggerTill, setLäggerTill] = useState(false);
  // Kostnaderna per flaska visar oftast bara standardvärdena, så de är dolda tills man vill se dem.
  const [visaKostnader, setVisaKostnader] = useState(false);
  const [lokalt, setLokalt] = useState<Record<string, Lokalt>>({});
  const inköp = p.pagaende[0];
  const låstInköp = !!inköp?.priser_last;
  const säljs = (r: PrisRad) => r.mottagare !== 'kran_aps' && !r.prov;
  // Låsta priser ligger i historiken; i arket står bara det som prissätts nu.
  const rader: Arkrad[] = [
    ...p.pagaende.filter(i => !i.priser_last).flatMap(i => i.rader.filter(säljs).map(r => ({ typ: 'inkop' as const, id: r.id, r, i }))),
    ...p.utkast.map(u => ({ typ: 'utkast' as const, id: u.id, u })),
  ];
  const valuta = p.valuta;
  const kolumner = visaKostnader ? 13 : 8;
  const kursInfo = inköp?.kurs_info ?? p.kurs_info;
  const kurs = kursInfo?.kurs;
  // Ett nyligen avslutat inköp med viner, när beställningen själv inte har några:
  // troligen samma beställning (t.ex. från Excel och läglistan).
  const gräns = new Date(Date.now() - 150 * 86400000).toISOString().slice(0, 10);
  const sammaKandidat = inköp && !inköp.rader.some(säljs) ? p.historik.find(h => h.datum >= gräns && h.rader.some(säljs)) : undefined;

  const sätt = (id: string, fält: keyof Lokalt, v: number | null | undefined) =>
    setLokalt(l => {
      const rad = { ...(l[id] || {}) };
      if (v === undefined) delete rad[fält]; else rad[fält] = v;
      return { ...l, [id]: rad };
    });
  const spara = (a: Arkrad, data: Record<string, unknown>) =>
    kör(() => (a.typ === 'inkop' ? ändraRad(a.id, data) : ändraPrisutkast(a.id, data)), 'Kunde inte spara.');
  const läggTill = async () => {
    const pris = tolka(nytt.pris);
    const vin = nytt.vin.trim();
    if (!vin || Number.isNaN(pris)) return false;
    const år = vin.match(/\b(19|20)\d{2}\b/)?.[0] || null;
    const iInköpet = inköp && !låstInköp && nytt.iInköpet;
    const ok = iInköpet
      ? await kör(() => skapaRad(inköp.id, { vin, argang: år, pris_valuta: pris, antal: 0 }), 'Kunde inte lägga till vinet.')
      : await kör(() => skapaPrisutkast(p.id, { vin, argang: år, pris_valuta: pris }), 'Kunde inte lägga till vinet.');
    if (ok) setNytt({ ...nytt, vin: '', pris: '' });
    return ok;
  };
  const kolumnbredder = (
    <colgroup>
      <col />
      {(visaKostnader ? [48, 60, 50, 44, 44, 46, 44, 56, 66, 72, 70, 60] : [60, 76, 56, 80, 110, 84, 76]).map((b, n) => <col key={n} style={{ width: `${b}px` }} />)}
    </colgroup>
  );

  return (
    <div>
      {/* ── Att prissätta nu ── */}
      <div style={{ borderLeft: `3px solid ${TEMA.accent}`, paddingLeft: '12px' }}>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'baseline', marginBottom: '4px' }}>
          <h3 style={avsnitt}>Att prissätta nu</h3>
          {valuta !== 'SEK' && kurs ? (
            <span style={{ fontSize: '12.5px', color: TEMA.inkSoft }} title="Kursen som inpriset räknas med. Den sparas tillsammans med listpriserna när de låses.">
              Kurs {valuta} <span style={{ color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace" }}>{tal2(kurs)}</span>
              {kursInfo?.kalla === 'riksbanken' && kursInfo.datum ? <span style={grå}> · Riksbanken {datumText(kursInfo.datum)}</span> : null}
              {kursInfo?.kalla === 'egen' ? <span style={grå}> · egen kurs</span> : null}
            </span>
          ) : null}
          <button style={{ ...knappSpök, padding: '5px 12px', fontSize: '12.5px', marginLeft: 'auto', ...(visaKostnader ? { borderColor: TEMA.inkSoft, color: TEMA.ink } : {}) }}
            onClick={() => setVisaKostnader(x => !x)} aria-pressed={visaKostnader}>
            {visaKostnader ? '▾ Dölj kostnader' : '▸ Visa alla kostnader'}
          </button>
        </div>

        {visaKostnader && <p style={{ ...grå, margin: '2px 0 6px' }}>Klicka i en cell och skriv för att ändra kostnaden för just det vinet. Grått är standardvärdet.</p>}
        {låstInköp && inköp && (
          <p style={{ fontSize: '12.5px', color: TEMA.inkSoft, margin: '4px 0 8px' }}>
            Priserna för beställningen låstes {datumText(inköp.priser_last)}{inköp.priser_last_av ? ` av ${inköp.priser_last_av}` : ''} och finns under
            Historik.{' '}
            <button style={{ ...länkknapp, fontSize: '12.5px' }}
              onClick={() => { if (window.confirm('Låsa upp priserna och räkna vidare på dem?')) kör(() => låsUppPriser(inköp.id), 'Kunde inte låsa upp.'); }}>
              Lås upp
            </button>
          </p>
        )}
        {sammaKandidat && inköp && (
          <div style={{ fontSize: '12.5px', color: TEMA.inkSoft, margin: '4px 0 8px', padding: '8px 10px', border: `1px dashed ${TEMA.border}`, borderRadius: '3px' }}>
            Beställningen har inga viner än. Inköpet {datumText(sammaKandidat.datum)} ({sammaKandidat.rader.filter(säljs).length} viner) ser ut att vara samma beställning.{' '}
            <button style={{ ...länkknapp, fontSize: '12.5px' }}
              onClick={() => { if (window.confirm(`Hämta vinerna från inköpet ${datumText(sammaKandidat.datum)} till beställningen? Inköpen slås ihop; allt sparas i händelseloggen.`))
                kör(() => slåIhop(inköp.id, sammaKandidat.id), 'Kunde inte slå ihop inköpen.'); }}>
              Hämta vinerna hit →
            </button>
          </div>
        )}
        {rader.length === 0 && !låstInköp && !sammaKandidat && (
          <p style={{ fontSize: '12.5px', color: TEMA.inkFaint, margin: '4px 0 8px' }}>Inga viner att prissätta. Lägg till ett nedan för att räkna på det.</p>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...tabell, minWidth: visaKostnader ? '840px' : '700px', tableLayout: 'fixed' }}>
            {kolumnbredder}
            {rader.length > 0 && (
              <thead>
                <tr>
                  <th style={{ ...th, padding: '6px 4px', fontSize: '11px' }}>Vin</th>
                  <th style={rubrikcell} title="Flaskor i beställningen">Fl</th>
                  <th style={rubrikcell}>Pris {valuta === 'EUR' ? '€' : valuta}</th>
                  <th style={rubrikcell} title="Frakt till lager, kr per flaska">Frakt</th>
                  {visaKostnader && <>
                    <th style={rubrikcell} title="Agent, procent av priset">Agent %</th>
                    <th style={rubrikcell} title="Liquid Services, kr per flaska">LS</th>
                    <th style={rubrikcell} title="Frakt till kund, kr per flaska">Fr. kund</th>
                    <th style={rubrikcell} title="Övrigt, kr per flaska">Övrigt</th>
                    <th style={rubrikcell} title="Punktskatt, kr per flaska; räknas från volym och alkohol">P-skatt</th>
                  </>}
                  <th style={rubrikcell} title="Totalt inpris, kr per flaska">Inpris</th>
                  <th style={rubrikcell} title={`Listpris med ${pct(förslagGräns)} marginal, uppåt till hela kronor`}>Föreslaget listpris</th>
                  <th style={rubrikcell} title="Listpriset ni räknar med. Tomt = det föreslagna.">Listpris</th>
                  <th style={rubrikcell} title="(Listpris − inpris) / listpris">Marginal</th>
                </tr>
              </thead>
            )}
            <tbody>
              {rader.map(a => {
                const k = a.typ === 'inkop' ? a.r.kalkyl : a.u.kalkyl;
                const vin = a.typ === 'inkop' ? a.r.vin : a.u.vin;
                const argang = a.typ === 'inkop' ? a.r.argang : a.u.argang;
                const fortnox = a.typ === 'inkop' ? a.r.listpris_fortnox : null;
                const iFortnox = a.typ === 'inkop' && a.r.listpris_kalla === 'fortnox';
                const v = a.typ === 'inkop' ? a.r : a.u;
                const l = lokalt[a.id] || {};
                const sparadPris = a.typ === 'inkop' ? (a.r.provpris ?? (iFortnox ? null : a.r.listpris)) : a.u.provpris;
                const listpris = l.provpris !== undefined ? l.provpris : sparadPris;
                const { inpris, förslag } = räknaOm(k, v.pris_valuta, l, förslagGräns);
                // Tomt listpris: det som redan gäller, annars det föreslagna.
                const standardpris = fortnox ?? förslag;
                const m = marginal(listpris ?? standardpris, inpris);
                const avFörslag = listpris == null && fortnox == null;
                const ärÖppen = öppen === a.id;
                const kant = ärÖppen ? 'none' : td.borderBottom;
                const cellProps = (fält: keyof Lokalt) => ({ onÄndra: (x: number | null | undefined) => sätt(a.id, fält, x) });
                return (
                  <React.Fragment key={a.id}>
                    <tr>
                      <td style={{ ...td, padding: '5px 4px', verticalAlign: 'middle', borderBottom: kant }}>
                        <button onClick={() => setÖppen(ö => (ö === a.id ? null : a.id))} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          color: TEMA.ink, fontFamily: "'Inter', sans-serif", fontSize: '13px', textAlign: 'left' }}>
                          {vin}{argang && !vin.includes(argang) ? ` ${argang}` : ''}
                        </button>
                        {a.typ === 'utkast' && <span style={{ ...grå, marginLeft: '6px' }} title="Finns inte i någon beställning">bara räkning</span>}
                        <TidigareRad t={a.typ === 'inkop' ? a.r.tidigare : a.u.tidigare} förslagGräns={förslagGräns} />
                      </td>
                      <td style={{ ...cell, borderBottom: kant }}>
                        {a.typ === 'inkop' ? <Cell värde={a.r.antal || null} etikett="Flaskor i beställningen" bredd={44} onSpara={x => spara(a, { antal: Math.round(x || 0) })} /> : null}
                      </td>
                      <td style={{ ...cell, borderBottom: kant }}>
                        <Cell värde={v.pris_valuta} etikett={`Pris per flaska, ${valuta}`} bredd={50} {...cellProps('pris_valuta')} onSpara={x => spara(a, { pris_valuta: x })} />
                      </td>
                      <td style={{ ...cell, borderBottom: kant }}><Cell värde={v.frakt_per_flaska} standard={tal2(k.frakt_till_lager)} etikett="Frakt till lager, kr/fl" bredd={42} {...cellProps('frakt_per_flaska')} onSpara={x => spara(a, { frakt_per_flaska: x })} /></td>
                      {visaKostnader && <>
                        <td style={{ ...cell, borderBottom: kant }}><Cell värde={v.agent_andel != null ? v.agent_andel * 100 : null} standard={tal2(k.agent_andel * 100)} etikett="Agent, % av priset" bredd={38}
                          {...cellProps('agent_procent')} onSpara={x => spara(a, { agent_andel: x == null ? null : x / 100 })} /></td>
                        <td style={{ ...cell, borderBottom: kant }}><Cell värde={v.ls} standard={tal2(k.ls)} etikett="LS, kr/fl" bredd={38} {...cellProps('ls')} onSpara={x => spara(a, { ls: x })} /></td>
                        <td style={{ ...cell, borderBottom: kant }}><Cell värde={v.frakt_till_kund} standard={tal2(k.frakt_till_kund)} etikett="Frakt till kund, kr/fl" bredd={40} {...cellProps('frakt_till_kund')} onSpara={x => spara(a, { frakt_till_kund: x })} /></td>
                        <td style={{ ...cell, borderBottom: kant }}><Cell värde={v.ovrigt} standard={tal2(k.ovrigt)} etikett="Övrigt, kr/fl" bredd={38} {...cellProps('ovrigt')} onSpara={x => spara(a, { ovrigt: x })} /></td>
                        <td style={{ ...cell, borderBottom: kant }}><Cell värde={v.punktskatt} standard={tal2(k.punktskatt)} etikett="Punktskatt, kr/fl" bredd={50} {...cellProps('punktskatt')} onSpara={x => spara(a, { punktskatt: x })} /></td>
                      </>}
                      <td style={{ ...cell, borderBottom: kant, fontWeight: 500, color: TEMA.ink }}>{kronor(inpris)}</td>
                      <td style={{ ...cell, borderBottom: kant, color: TEMA.inkSoft }}>{förslag ?? '–'}</td>
                      <td style={{ ...cell, borderBottom: kant }}>
                        <Cell värde={sparadPris} standard={standardpris != null ? String(Math.round(standardpris)) : undefined} etikett="Listpris, kr exkl. moms" bredd={60}
                          onÄndra={x => sätt(a.id, 'provpris', x)} onSpara={x => spara(a, { provpris: x })} />
                      </td>
                      <td style={{ ...cell, borderBottom: kant, color: m == null || avFörslag ? TEMA.inkFaint : m < förslagGräns ? RÖD : TEMA.ink }}
                        title={avFörslag ? 'Marginalen vid det föreslagna listpriset' : 'Marginalen vid listpriset'}>
                        {pct(m)}
                      </td>
                    </tr>
                    {ärÖppen && (
                      <tr><td colSpan={kolumner} style={{ ...td, padding: '2px 4px 12px 14px' }}>
                        {a.typ === 'inkop' && ändrarRad?.id === a.id ? (
                          <RadFormulär utkast={ändrarRad.u} setUtkast={u => setÄndrarRad({ id: a.id, u })} valuta={valuta} onAvbryt={() => setÄndrarRad(null)}
                            onSpara={async () => { if (await kör(() => ändraRad(a.id, utkastTillData(ändrarRad.u)), 'Kunde inte spara vinet.')) setÄndrarRad(null); }} />
                        ) : a.typ === 'utkast' && ändrarUtkast?.id === a.id ? (
                          <UtkastFormulär f={ändrarUtkast.f} setF={f => setÄndrarUtkast({ id: a.id, f })} onAvbryt={() => setÄndrarUtkast(null)}
                            onSpara={async () => {
                              const f = ändrarUtkast.f;
                              const n = (t: string) => (t.trim() === '' ? null : Number(t.replace(',', '.')));
                              if (await kör(() => ändraPrisutkast(a.id, { vin: f.vin.trim(), typ: f.typ || null, argang: f.argang.trim() || null,
                                volym_cl: n(f.volym_cl) ?? 75, alkoholhalt: n(f.alkoholhalt) }), 'Kunde inte spara vinet.')) setÄndrarUtkast(null);
                            }} />
                        ) : (
                          <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <Uppställning k={k} pris={v.pris_valuta} valuta={valuta} />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px' }}>
                              <span style={grå}>
                                {[v.volym_cl !== 75 ? `${v.volym_cl} cl` : '75 cl', v.alkoholhalt ? `${v.alkoholhalt} %` : 'alkohol saknas',
                                  a.typ === 'utkast' && a.u.typ ? VINTYPNAMN[a.u.typ as Vintyp] : null].filter(Boolean).join(' · ')}
                              </span>
                              <button style={{ ...länkknapp, fontSize: '12.5px', textAlign: 'left' }}
                                onClick={() => a.typ === 'inkop' ? setÄndrarRad({ id: a.id, u: radTillUtkast(a.r) }) : setÄndrarUtkast({ id: a.id, f: tillFält(a.u) })}>
                                Ändra namn, volym, alkohol …
                              </button>
                              <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint, textAlign: 'left' }}
                                onClick={() => { if (window.confirm(`Ta bort ${vin}?`)) kör(() => (a.typ === 'inkop' ? taBortRad(a.id) : taBortPrisutkast(a.id)), 'Kunde inte ta bort.'); }}>
                                Ta bort
                              </button>
                            </div>
                          </div>
                        )}
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Lägg till ett vin och lås priserna. Namn och pris räcker; resten fylls i direkt i arket. */}
        {läggerTill ? (
          <form onSubmit={async e => { e.preventDefault(); if (await läggTill()) setLäggerTill(false); }}
            style={{ border: `1px solid ${TEMA.border}`, borderRadius: '4px', padding: '12px 14px', margin: '10px 0 4px', background: TEMA.bg }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ flex: '1 1 260px' }}>
                <span style={{ ...etikett, marginBottom: '4px' }}>Vin och årgång</span>
                <input style={{ ...fält, padding: '6px 10px', fontSize: '13px' }} placeholder="t.ex. Godello 2025" autoFocus
                  value={nytt.vin} onChange={e => setNytt({ ...nytt, vin: e.target.value })} />
              </label>
              <label style={{ width: '150px' }}>
                <span style={{ ...etikett, marginBottom: '4px' }}>Pris per flaska, {valuta === 'EUR' ? '€' : valuta}</span>
                <input style={{ ...fält, padding: '6px 10px', fontSize: '13px', textAlign: 'right' }} inputMode="decimal"
                  value={nytt.pris} onChange={e => setNytt({ ...nytt, pris: e.target.value })} />
              </label>
            </div>
            {inköp && !låstInköp && (
              <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginTop: '12px', fontSize: '13px', color: TEMA.inkSoft }}>
                <label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" style={{ accentColor: TEMA.accent }} checked={nytt.iInköpet} onChange={() => setNytt({ ...nytt, iInköpet: true })} />
                  Lägg till i beställningen
                </label>
                <label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" style={{ accentColor: TEMA.accent }} checked={!nytt.iInköpet} onChange={() => setNytt({ ...nytt, iInköpet: false })} />
                  Bara räkna på det, utan att beställa
                </label>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button type="submit" style={{ ...knapp, padding: '6px 14px', opacity: nytt.vin.trim() ? 1 : 0.6 }} disabled={!nytt.vin.trim()}>Lägg till vinet</button>
              <button type="button" style={{ ...knappSpök, padding: '6px 14px' }} onClick={() => setLäggerTill(false)}>Avbryt</button>
            </div>
          </form>
        ) : (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 0 2px' }}>
            <button style={{ ...knappSpök, padding: '6px 14px', fontSize: '13px' }} onClick={() => setLäggerTill(true)}>+ Lägg till ett vin</button>
            {inköp && !låstInköp && inköp.rader.some(säljs) && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <span style={{ ...grå, maxWidth: '300px', textAlign: 'right' }}>När ni är överens med producenten: lås listpriserna och kursen. De sparas i Historik.</span>
                <button style={{ ...knapp, padding: '8px 18px', fontSize: '13.5px' }}
                  onClick={() => kör(() => låsPriser(inköp.id), 'Kunde inte låsa priserna.')}>🔒 Lås priserna</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// En producent eller kandidat: rubrik och kalkylarket.
function ProducentGrupp({ p, förslagGräns, öppenFrån, kör }: {
  p: PrisProducent; förslagGräns: number; öppenFrån: boolean; kör: Kör;
}) {
  const [öppen, setÖppen] = useState(öppenFrån);
  useEffect(() => { setÖppen(öppenFrån); }, [öppenFrån]);
  return (
    <div style={{ ...kort, padding: öppen ? '14px 18px 14px' : '11px 18px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <button onClick={() => setÖppen(ö => !ö)} aria-expanded={öppen} style={{
          display: 'flex', alignItems: 'baseline', gap: '10px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Inter', sans-serif",
        }}>
          <span style={{ color: TEMA.inkFaint, width: '10px', fontSize: '11px' }}>{öppen ? '▾' : '▸'}</span>
          <span style={{ ...underrubrik, margin: 0 }}>{p.namn}</span>
        </button>
        {p.kandidat && <span style={{ ...märke('grå'), alignSelf: 'center' }}>Kandidat</span>}
      </div>
      {öppen && <div style={{ marginTop: '14px' }}><Kalkylark p={p} förslagGräns={förslagGräns} kör={kör} /></div>}
    </div>
  );
}

// ── Historiken ───────────────────────────────────────────────────────────────
// Alla låsta prissättningar och tidigare inköp, nyast först. Vin med låst pris
// som inte finns som artikel i Fortnox märks; det är det som ska läggas upp.
const säljbar = (r: PrisRad) => r.mottagare !== 'kran_aps' && !r.prov;
const datumFör = (h: PrisInköp) => (h.priser_last ? h.priser_last.slice(0, 10) : h.datum) || '';

function Historik({ data, förslagGräns, onFel }: { data: Prissättning; förslagGräns: number; onFel: (t: string) => void }) {
  const [sök, setSök] = useState('');
  const [antal, setAntal] = useState(15);
  const [exporterar, setExporterar] = useState(false);
  const [baraEj, setBaraEj] = useState(false);
  const ejUpplagd = (r: PrisRad) => säljbar(r) && r.last_listpris != null && !r.fortnox_artikel_nr;
  const poster = useMemo(() => data.producenter
    .flatMap(p => [...p.pagaende.filter(i => i.priser_last), ...p.historik]
      .filter(h => h.rader.some(säljbar))
      .map(h => ({ p, h, datum: datumFör(h) })))
    .sort((a, b) => b.datum.localeCompare(a.datum)), [data]);
  const s = sök.trim().toLowerCase();
  const synliga = poster
    .filter(x => !s || [x.p.namn, x.p.land, ...x.h.rader.map(r => r.vin)].join(' ').toLowerCase().includes(s))
    .filter(x => !baraEj || x.h.rader.some(ejUpplagd));
  const ejUpplagda = poster.reduce((n, x) => n + x.h.rader.filter(ejUpplagd).length, 0);
  const bredder = <colgroup><col /><col style={{ width: '70px' }} /><col style={{ width: '80px' }} /><col style={{ width: '84px' }} /><col style={{ width: '84px' }} /><col style={{ width: '76px' }} /></colgroup>;
  async function exportera() {
    setExporterar(true);
    try { await laddaNerHistorik(); } catch (e) { onFel(felText(e, 'Kunde inte exportera.')); } finally { setExporterar(false); }
  }
  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
        <input style={{ ...fält, padding: '7px 12px', fontSize: '13.5px', width: '320px', maxWidth: '100%' }}
          placeholder="Sök producent, land eller vin" value={sök} onChange={e => setSök(e.target.value)} aria-label="Sök i historiken" />
        {ejUpplagda > 0 && (
          <button onClick={() => setBaraEj(x => !x)} title="Visa bara det som ska läggas upp i Fortnox"
            style={{ ...märke('guld'), cursor: 'pointer', background: baraEj ? 'rgba(224,168,62,0.18)' : 'transparent' }}>
            {ejUpplagda} ej i Fortnox{baraEj ? ' ×' : ''}
          </button>
        )}
        <button style={{ ...knappSpök, padding: '6px 14px', fontSize: '12.5px', marginLeft: 'auto', opacity: exporterar ? 0.6 : 1 }} disabled={exporterar} onClick={exportera}>
          {exporterar ? 'Exporterar …' : 'Exportera till Excel'}
        </button>
      </div>
      {synliga.length === 0 && <p style={{ fontSize: '13px', color: TEMA.inkFaint }}>{s ? 'Inget i historiken matchar sökningen.' : 'Ingen historik än. Låsta priser hamnar här.'}</p>}
      {synliga.length > 0 && (
        <div style={{ ...kort, padding: '14px 18px' }}>
          <table style={{ ...tabell, tableLayout: 'fixed' }}>
            {bredder}
            <thead><tr>
              <th style={{ ...th, padding: '4px', fontSize: '11px' }}>Vin</th>
              <th style={rubrikcell}>Fl</th><th style={rubrikcell}>Pris</th><th style={rubrikcell}>Inpris</th>
              <th style={rubrikcell} title="Listpriset som gällde: Fortnox, annars det låsta">Listpris</th><th style={rubrikcell}>Marginal</th>
            </tr></thead>
          </table>
          {synliga.slice(0, antal).map(({ p, h, datum }) => {
            const hr = h.rader.filter(säljbar);
            return (
              <div key={h.id} style={{ marginTop: '14px' }}>
                <div style={{ fontSize: '12.5px', color: TEMA.inkSoft, padding: '4px 0', borderBottom: `1px solid ${TEMA.border}`, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint }}>{datumText(datum)}</span>
                  <Link to={`/inkop/producenter/${p.id}`} style={{ color: TEMA.ink, textDecoration: 'none', fontSize: '14px' }}>{p.namn}</Link>
                  <span>{h.priser_last ? `priserna låstes${h.priser_last_av ? ` av ${h.priser_last_av}` : ''}` : h.importerat ? 'från Excel' : `inköp ${h.statusnamn.toLowerCase()}`}
                    {h.valuta !== 'SEK' && (hr[0]?.last_kalkyl?.kurs ?? h.kurs) ? ` · kurs ${h.valuta} ${tal2(hr[0]?.last_kalkyl?.kurs ?? h.kurs)}` : ''}
                    {' · '}{hr.reduce((n, r) => n + r.antal, 0).toLocaleString('sv-SE')} fl</span>
                </div>
                <table style={{ ...tabell, tableLayout: 'fixed' }}>
                  {bredder}
                  <tbody>
                    {hr.filter(r => !baraEj || ejUpplagd(r)).map(r => {
                      const lp = r.listpris_fortnox ?? r.last_listpris ?? null;
                      const m = marginal(lp, h.priser_last && r.last_inpris != null ? r.last_inpris : r.kalkyl.inpris_totalt);
                      return (
                        <tr key={r.id}>
                          <td style={{ ...td, padding: '5px 4px', fontSize: '13px' }}>
                            {r.vin}{r.argang && !r.vin.includes(r.argang) ? ` ${r.argang}` : ''}
                            {ejUpplagd(r) && <span style={{ ...märke('guld'), marginLeft: '8px', fontSize: '10px', padding: '0 6px' }} title="Priset är låst men vinet finns inte som artikel i Fortnox än">ej i Fortnox</span>}
                          </td>
                          <td style={{ ...cell, color: TEMA.inkFaint }}>{r.antal.toLocaleString('sv-SE')}</td>
                          <td style={{ ...cell, color: TEMA.inkFaint }}>{valutabelopp(r.pris_valuta ?? null, h.valuta)}</td>
                          <td style={{ ...cell, color: TEMA.inkSoft }}>{kronor(h.priser_last && r.last_inpris != null ? r.last_inpris : r.kalkyl.inpris_totalt)}</td>
                          <td style={{ ...cell, color: lp != null ? TEMA.ink : TEMA.inkFaint }}>{lp != null ? `${kronor(lp, 0)} kr` : '–'}</td>
                          <td style={{ ...cell, color: m == null ? TEMA.inkFaint : m < förslagGräns ? RÖD : TEMA.inkSoft }}>{pct(m)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
          {synliga.length > antal && (
            <button style={{ ...länkknapp, fontSize: '12.5px', marginTop: '12px' }} onClick={() => setAntal(n => n + 20)}>Visa fler ({synliga.length - antal} kvar)</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Skatteverkets satser ─────────────────────────────────────────────────────
// Satserna som de står hos Skatteverket, som jämförelse med inställningarna.
// Ändras vid årsskiften; uppdateras här när Skatteverket publicerar nya.
const SKATTEVERKET = {
  galler_fran: '2024-01-01',
  kontrollerad: '2026-10-29',
  kalla: 'https://www.skatteverket.se/foretag/skatterochavdrag/punktskatter/alkoholskatt/skattesatserforalkohol.4.4a47257e143e26725aecb5.html',
  satser: [
    { grupp: 'Vin', namn: 'Över 8,5 %, upp till 15 %', kr: 29.58, enhet: 'kr per liter', till_alkohol: 15 },
    { grupp: 'Vin', namn: 'Över 15 %, upp till 18 %', kr: 61.9, enhet: 'kr per liter', till_alkohol: 18 },
    { grupp: 'Mellanklassprodukter', namn: 'Över 15 %, upp till 22 %, t.ex. starkvin och likör', kr: 61.9, enhet: 'kr per liter', till_alkohol: 22 },
    { grupp: 'Sprit', namn: 'Över 22 %', kr: 526.97, enhet: 'kr per liter ren alkohol', till_alkohol: 100, per_ren_alkohol: true },
  ],
  ovriga: 'Lägre satser för vin under 8,5 %: 0 kr upp till 2,25 %, 10,38 kr upp till 4,5 %, 15,34 kr upp till 7 % och 21,12 kr upp till 8,5 % per liter.',
};

function SkatteverketsSatser({ data, kör }: { data: Prissättning; kör: Kör }) {
  const { profil } = useAuth();
  const admin = profil?.roll === 'admin';
  const egna = data.punktskatt || [];
  const motsvarande = (s: typeof SKATTEVERKET.satser[number]) =>
    egna.find(e => (s.per_ren_alkohol ? !!e.per_ren_alkohol : !e.per_ren_alkohol && Number(e.till_alkohol) === s.till_alkohol));
  const avviker = SKATTEVERKET.satser.filter(s => { const e = motsvarande(s); return !e || Math.abs(Number(e.kr_per_liter) - s.kr) > 0.005; });
  function använd() {
    if (!window.confirm('Ersätta punktskatterna i standardvärdena med Skatteverkets satser? Låsta priser påverkas inte.')) return;
    const nya = SKATTEVERKET.satser.map(s => {
      const e = motsvarande(s);
      return { till_alkohol: s.till_alkohol, kr_per_liter: s.kr, namn: e?.namn || `${s.grupp} ${s.namn.toLowerCase()}`, ...(s.per_ren_alkohol ? { per_ren_alkohol: true } : {}) };
    });
    kör(() => sparaInställningar({ punktskatt: nya }), 'Kunde inte spara punktskatterna.');
  }
  return (
    <div style={{ ...kort, maxWidth: '680px', borderColor: avviker.length ? 'rgba(224,168,62,0.45)' : TEMA.border }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <h3 style={{ ...avsnitt, fontSize: '16px' }}>Punktskatt enligt Skatteverket</h3>
        <span style={grå}>Gäller från {datumText(SKATTEVERKET.galler_fran)} · kontrollerad {datumText(SKATTEVERKET.kontrollerad)}</span>
      </div>
      <div style={{ marginTop: '10px' }}>
        {SKATTEVERKET.satser.map(s => {
          const e = motsvarande(s);
          const fel = !e || Math.abs(Number(e.kr_per_liter) - s.kr) > 0.005;
          return (
            <div key={s.grupp + s.namn} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', padding: '8px 0', borderTop: `1px solid ${TEMA.border}`, fontSize: '13.5px', alignItems: 'baseline' }}>
              <span style={{ color: TEMA.ink }}>{s.grupp} <span style={grå}>{s.namn}</span></span>
              <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <span style={{ color: TEMA.ink }}>{kronor(s.kr, 2)} {s.enhet}</span>
                {fel && <span style={{ display: 'block', fontSize: '11.5px', color: TEMA.accent }}>{e ? `ni räknar med ${kronor(Number(e.kr_per_liter), 2)}` : 'saknas i era värden'}</span>}
              </span>
            </div>
          );
        })}
      </div>
      <p style={{ ...grå, margin: '10px 0 0', lineHeight: 1.5 }}>{SKATTEVERKET.ovriga}</p>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginTop: '12px', fontSize: '12.5px' }}>
        <a href={SKATTEVERKET.kalla} target="_blank" rel="noreferrer" style={{ color: TEMA.accent, textDecoration: 'none' }}>Källa: Skatteverket, Skattesatser för alkohol ↗</a>
        {avviker.length > 0 && (admin
          ? <button style={{ ...knappSpök, padding: '5px 12px', fontSize: '12.5px', marginLeft: 'auto', borderColor: TEMA.accent, color: TEMA.accent }} onClick={använd}>Använd Skatteverkets satser</button>
          : <span style={{ ...grå, marginLeft: 'auto' }}>{avviker.length} {avviker.length === 1 ? 'sats skiljer sig' : 'satser skiljer sig'} från era värden. Kontakta admin.</span>)}
      </div>
    </div>
  );
}

// ── Standardvärden ───────────────────────────────────────────────────────────
// Alla ser värdena; bara admin kan ändra dem (backend kontrollerar också).
function Standardvärden({ data, kör }: { data: Prissättning; kör: Kör }) {
  const { profil } = useAuth();
  const admin = profil?.roll === 'admin';
  const start = () => ({
    frakt_till_lager: tal2(data.standard.frakt_till_lager ?? 10), ls: tal2(data.standard.ls),
    frakt_till_kund: tal2(data.standard.frakt_till_kund), ovrigt: tal2(data.standard.ovrigt),
    foreslagen_marginal: tal2(data.foreslagen_marginal),
    lagergrans: tal2(data.standard.lagergrans ?? 60),
    punktskatt: (data.punktskatt || []).map(p => tal2(p.kr_per_liter)),
  });
  const [ändrar, setÄndrar] = useState(false);
  const [f, setF] = useState(start);
  const [fel, setFel] = useState<string | null>(null);
  const rad: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '8px 0', borderTop: `1px solid ${TEMA.border}`, fontSize: '13.5px', minHeight: '22px' };
  const ruta = (värde: string, sätt: (v: string) => void, enhet: string, etikettText: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: TEMA.inkSoft }}>
      <input style={{ ...fält, width: '76px', padding: '4px 8px', fontSize: '13px', textAlign: 'right' }} inputMode="decimal" aria-label={etikettText}
        value={värde} onChange={e => sätt(e.target.value)} />
      {enhet}
    </span>
  );
  async function spara() {
    const tal = (t: string) => Number(t.replace(/\s/g, '').replace(',', '.'));
    const kalk = { frakt_till_lager: tal(f.frakt_till_lager), ls: tal(f.ls), frakt_till_kund: tal(f.frakt_till_kund), ovrigt: tal(f.ovrigt), foreslagen_marginal: tal(f.foreslagen_marginal), lagergrans: Math.round(tal(f.lagergrans)) };
    if (Object.values(kalk).some(v => !Number.isFinite(v) || v < 0) || f.punktskatt.some(t => !Number.isFinite(tal(t)))) {
      setFel('Fyll i alla värden som tal.'); return;
    }
    setFel(null);
    const punktskatt = (data.punktskatt || []).map((p, n) => ({ ...p, kr_per_liter: tal(f.punktskatt[n]) }));
    if (await kör(() => sparaInställningar({ kalkyl: kalk, punktskatt }), 'Kunde inte spara standardvärdena.')) setÄndrar(false);
  }
  const visa = (v: number | null | undefined, enhet: string) => `${kronor(v, v != null && v % 1 ? 2 : 0)} ${enhet}`;
  return (
    <div style={{ ...kort, maxWidth: '680px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
        <p style={{ fontSize: '13px', color: TEMA.inkSoft, margin: 0, lineHeight: 1.5 }}>
          Kostnaderna som används när ett vin eller en beställning inte har egna värden. Ett eget värde skrivs in direkt i arket
          under Viner att prissätta och gäller då bara för det vinet.
        </p>
        {admin && !ändrar && <button style={{ ...knappSpök, padding: '6px 14px', whiteSpace: 'nowrap' }} onClick={() => { setF(start()); setÄndrar(true); }}>Ändra</button>}
      </div>
      <div style={rad}><span style={{ color: TEMA.ink }}>Frakt till lager</span>
        {ändrar ? ruta(f.frakt_till_lager, v => setF({ ...f, frakt_till_lager: v }), 'kr per flaska', 'Frakt till lager')
          : <span style={{ color: TEMA.inkSoft, textAlign: 'right' }}>Beställningens frakt delad på flaskorna, annars {visa(data.standard.frakt_till_lager ?? 10, 'kr per flaska')}</span>}</div>
      <div style={rad}><span style={{ color: TEMA.ink }}>Agent</span>
        <span style={{ color: TEMA.inkSoft, textAlign: 'right' }}>Producentens agentandel av priset, sätts på producentkortet</span></div>
      <div style={rad}><span style={{ color: TEMA.ink }}>LS (Liquid Services)</span>
        {ändrar ? ruta(f.ls, v => setF({ ...f, ls: v }), 'kr per flaska', 'LS') : <span style={{ color: TEMA.inkSoft }}>{visa(data.standard.ls, 'kr per flaska')}</span>}</div>
      <div style={rad}><span style={{ color: TEMA.ink }}>Frakt till kund</span>
        {ändrar ? ruta(f.frakt_till_kund, v => setF({ ...f, frakt_till_kund: v }), 'kr per flaska', 'Frakt till kund') : <span style={{ color: TEMA.inkSoft }}>{visa(data.standard.frakt_till_kund, 'kr per flaska')}</span>}</div>
      <div style={rad}><span style={{ color: TEMA.ink }}>Övrigt</span>
        {ändrar ? ruta(f.ovrigt, v => setF({ ...f, ovrigt: v }), 'kr per flaska', 'Övrigt') : <span style={{ color: TEMA.inkSoft }}>{visa(data.standard.ovrigt, 'kr per flaska')}</span>}</div>
      {(data.punktskatt || []).map((p, n) => (
        <div key={n} style={rad}><span style={{ color: TEMA.ink }}>Punktskatt <span style={grå}>{p.namn}</span></span>
          {ändrar ? ruta(f.punktskatt[n], v => setF({ ...f, punktskatt: f.punktskatt.map((x, m) => (m === n ? v : x)) }), p.per_ren_alkohol ? 'kr per liter ren alkohol' : 'kr per liter', `Punktskatt ${p.namn}`)
            : <span style={{ color: TEMA.inkSoft }}>{visa(p.kr_per_liter, p.per_ren_alkohol ? 'kr per liter ren alkohol' : 'kr per liter')}</span>}</div>
      ))}
      <div style={rad}><span style={{ color: TEMA.ink }}>Föreslaget listpris</span>
        {ändrar ? ruta(f.foreslagen_marginal, v => setF({ ...f, foreslagen_marginal: v }), '% marginal', 'Föreslagen marginal')
          : <span style={{ color: TEMA.inkSoft }}>Ger {pct(data.foreslagen_marginal)} marginal, avrundat uppåt</span>}</div>
      <div style={rad}><span style={{ color: TEMA.ink }}>Lager som tar slut <span style={grå}>visas på startsidan</span></span>
        {ändrar ? ruta(f.lagergrans, v => setF({ ...f, lagergrans: v }), 'flaskor', 'Lager som tar slut, gräns i flaskor')
          : <span style={{ color: TEMA.inkSoft }}>Färre än {kronor(data.standard.lagergrans ?? 60, 0)} flaskor kvar</span>}</div>
      {fel && <p style={{ color: RÖD, fontSize: '13px', margin: '10px 0 0' }}>{fel}</p>}
      {ändrar && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
          <button style={knapp} onClick={spara}>Spara</button>
          <button style={knappSpök} onClick={() => { setÄndrar(false); setFel(null); }}>Avbryt</button>
          <span style={{ ...grå, alignSelf: 'center' }}>Gäller alla viner som inte har egna värden, även i pågående inköp.</span>
        </div>
      )}
      {!admin && <p style={{ ...grå, margin: '14px 0 0' }}>Vill du ändra dessa värden, kontakta admin.</p>}
    </div>
  );
}

type Flik = 'prissatta' | 'historik' | 'standard';
type Filter = 'pagang' | 'alla' | 'kandidater';
const FILTERNAMN: Record<Filter, string> = { pagang: 'På ingång', alla: 'Alla', kandidater: 'Kandidater' };

// Prissättningen är hemlig och finns inte i demon (backend svarar 404 där).
export default function InkopPrissattning() {
  const demo = useDemo();
  return demo ? <Navigate to="/inkop" replace /> : <Prissättningssida />;
}

function Prissättningssida() {
  const [data, setData] = useState<Prissättning | null>(null);
  const [params, setParams] = useSearchParams();
  const vald = params.get('producent');
  const [filter, setFilter] = useState<Filter>(vald ? 'alla' : 'pagang');
  const [sök, setSök] = useState('');
  const [flik, setFlik] = useState<Flik>(params.get('flik') === 'historik' ? 'historik' : params.get('flik') === 'standard' ? 'standard' : 'prissatta');
  const [fel, setFel] = useState<string | null>(null);

  const ladda = useCallback(() => hämtaPrissättning().then(r => setData(r.data)).catch(e => setFel(felText(e, 'Kunde inte hämta prissättningen.'))), []);
  useEffect(() => { ladda(); }, [ladda]);

  const kör = useCallback(async (f: () => Promise<unknown>, felmeddelande: string) => {
    setFel(null);
    try {
      await f();
      await ladda();
      return true;
    } catch (e) {
      setFel(felText(e, felmeddelande));
      return false;
    }
  }, [ladda]);

  // Sökningen gäller producent, land och vinernas namn.
  const synliga = useMemo(() => {
    if (!data) return [];
    const s = sök.trim().toLowerCase();
    return data.producenter.filter(p => {
      if (vald) return p.id === vald;
      if (s) {
        const text = [p.namn, p.land, p.region, ...p.pagaende.flatMap(i => i.rader.map(r => r.vin)), ...p.utkast.map(u => u.vin),
          ...p.historik.flatMap(i => i.rader.map(r => r.vin))].filter(Boolean).join(' ').toLowerCase();
        return text.includes(s);
      }
      if (filter === 'pagang') return p.pagaende.length > 0 || p.utkast.length > 0;
      if (filter === 'kandidater') return p.kandidat;
      return true;
    });
  }, [data, sök, filter, vald]);

  const länder = useMemo(() => {
    const g: { land: string; producenter: PrisProducent[] }[] = [];
    synliga.forEach(p => {
      const sista = g[g.length - 1];
      if (sista && sista.land === p.land) sista.producenter.push(p);
      else g.push({ land: p.land, producenter: [p] });
    });
    return g;
  }, [synliga]);

  const filterknapp = (aktiv: boolean): React.CSSProperties => ({
    ...knappSpök, padding: '5px 12px', fontSize: '12.5px',
    ...(aktiv ? { borderColor: TEMA.accent, color: TEMA.accent } : {}),
  });
  // Allt är ihopfällt när man kommer in. En sökning eller en länk till en producent öppnar.
  const öppnaAlla = !!sök.trim() || !!vald;

  return (
    <div style={sida}>
      <style>{`.vs-cell::placeholder { color: ${TEMA.inkSoft}; opacity: 0.8; } .vs-cell:hover { border-color: ${TEMA.inkFaint} !important; }`}</style>
      <h1 style={{ ...rubrik, marginBottom: '14px' }}>Prissättning</h1>
      <div style={{ display: 'flex', gap: '24px', borderBottom: `1px solid ${TEMA.border}`, marginBottom: '16px' }}>
        {([['prissatta', 'Viner att prissätta'], ['historik', 'Historik'], ['standard', 'Standardvärden']] as [Flik, string][]).map(([f, text]) => (
          <button key={f} onClick={() => setFlik(f)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 10px', marginBottom: '-1px',
            fontFamily: "'Inter', sans-serif", fontSize: '14.5px', color: flik === f ? TEMA.ink : TEMA.inkFaint,
            borderBottom: `2px solid ${flik === f ? TEMA.accent : 'transparent'}`,
          }}>{text}</button>
        ))}
      </div>
      {flik !== 'standard' && <p style={{ fontSize: '13.5px', color: TEMA.inkSoft, margin: '0 0 16px', maxWidth: '820px', lineHeight: 1.5 }}>
        {flik === 'prissatta' ? <>
          Här finns producenterna som har ett inköp på gång, samma som under Pågående inköp, och viner som lagts till bara för att räkna på.
          Prissättningen görs medan inköpet är i dialog med producenten. Räkna fram listpriserna och lås dem när ni är överens. Då bockas
          "Priser avtalade" av på inköpet, och priserna flyttar till Historik.
        </> : <>
          Alla låsta prissättningar och tidigare inköp, det senaste först. När vinet har kommit till lagret läggs det upp i Fortnox med
          listpriset härifrån. Vin som inte finns i Fortnox än är märkta, och märket försvinner när artikeln finns.
        </>}
      </p>}

      {flik === 'prissatta' && <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        <input style={{ ...fält, padding: '7px 12px', fontSize: '13.5px', width: '320px', maxWidth: '100%' }}
          placeholder="Sök producent, land eller vin" value={sök} onChange={e => { setSök(e.target.value); if (vald) setParams({}); }} aria-label="Sök" />
        {vald && <button style={filterknapp(true)} onClick={() => setParams({})}>En producent ×</button>}
        {(Object.keys(FILTERNAMN) as Filter[]).map(f => (
          <button key={f} style={filterknapp(!vald && !sök.trim() && filter === f)} onClick={() => { setFilter(f); setSök(''); if (vald) setParams({}); }}>{FILTERNAMN[f]}</button>
        ))}
      </div>}

      {fel && <p style={{ color: RÖD, fontSize: '13.5px' }}>{fel}</p>}
      {!data && !fel && <p style={{ fontSize: '13px', color: TEMA.inkFaint }}>Laddar...</p>}
      {data && flik === 'standard' && <><Standardvärden data={data} kör={kör} /><SkatteverketsSatser data={data} kör={kör} /></>}
      {data && flik === 'historik' && <Historik data={data} förslagGräns={data.foreslagen_marginal} onFel={setFel} />}
      {data && flik === 'prissatta' && (
        <>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '21px', color: TEMA.ink, fontWeight: 400, margin: '6px 0 14px' }}>
            Viner att prissätta <span style={{ fontSize: '14px', color: TEMA.inkFaint }}>{synliga.length} {synliga.length === 1 ? 'producent' : 'producenter'}</span>
          </h2>
          {synliga.length === 0 && <p style={{ fontSize: '13px' }}>Inga producenter att visa.</p>}
          {länder.map(l => (
            <section key={l.land} style={{ marginBottom: '18px' }}>
              <h2 style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint, textTransform: 'uppercase',
                letterSpacing: '0.08em', fontWeight: 400, margin: '0 0 8px' }}>{l.land}</h2>
              {l.producenter.map(p => (
                <ProducentGrupp key={p.id} p={p} förslagGräns={data.foreslagen_marginal} kör={kör}
                  öppenFrån={öppnaAlla} />
              ))}
            </section>
          ))}

        </>
      )}
    </div>
  );
}
